import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Shield, Globe } from 'lucide-react';
import { HERO_BG, GRAD_TEXT, EYEBROW } from './shared';

const Hero = ({ onStart }) => (
  <section style={{
    position: 'relative', minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', padding: '7rem 1.5rem 5rem',
  }}>
    <div className="ken-burns" style={{
      position: 'absolute', inset: '-4%', zIndex: 0,
      backgroundImage: `url(${HERO_BG})`,
      backgroundSize: 'cover', backgroundPosition: 'center',
    }} />
    <div style={{
      position: 'absolute', inset: 0, zIndex: 1,
      background: 'linear-gradient(to bottom, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0.95) 100%)',
    }} />
    <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: '780px' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}>
        <span style={{ ...EYEBROW, display: 'inline-block', marginBottom: '2rem' }}>
          AI-Powered Migration Assessment
        </span>
      </motion.div>
      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }} style={{
        fontFamily: 'var(--font-heading)', fontSize: 'clamp(2.5rem, 6vw, 4.25rem)',
        fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.02, color: '#302B2C', marginBottom: '1.75rem',
      }}>
        Discover where you<br /><span style={GRAD_TEXT}>can live and work</span>
      </motion.h1>
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6, ease: 'easeOut' }} style={{
        fontFamily: 'var(--font-body)', fontSize: 'clamp(1rem, 2vw, 1.25rem)',
        color: 'rgba(48,43,44,0.72)', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto 2.75rem',
      }}>
        Upload your CV and get a personalised migration assessment across 20+ countries in under 5 minutes. Powered by real immigration rules.
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8, ease: 'easeOut' }} style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <button onClick={onStart} data-testid="hero-start-btn" style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 36px',
          background: 'linear-gradient(135deg, #F67425 0%, #BB0518 100%)', color: '#fff', border: 'none', borderRadius: '2px',
          fontFamily: 'var(--font-heading)', fontSize: '1.0625rem', fontWeight: 700, cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(246,116,37,0.25)', transition: 'transform 0.2s, box-shadow 0.3s',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(246,116,37,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(246,116,37,0.25)'; }}
        >
          Start My Assessment <ArrowRight size={18} strokeWidth={2} />
        </button>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.95 }} style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '1.25rem',
        padding: '6px 14px', borderRadius: '2px',
        background: 'rgba(11,129,90,0.07)', border: '1px solid rgba(11,129,90,0.25)',
      }} data-testid="hero-urgency-tag">
        <span className="pulse-dot" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0B815A' }} />
        <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.78rem', fontWeight: 700, color: '#0B815A' }}>
          Free assessment — limited slots today
        </span>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 1.1 }} style={{
        display: 'flex', justifyContent: 'center', gap: '2.5rem', flexWrap: 'wrap', marginTop: '3.5rem',
      }}>
        {[
          { icon: <Zap size={14} strokeWidth={2} />, text: 'Results in 5 minutes' },
          { icon: <Shield size={14} strokeWidth={2} />, text: '256-bit encrypted' },
          { icon: <Globe size={14} strokeWidth={2} />, text: '20+ countries covered' },
        ].map(t => (
          <span key={t.text} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            fontFamily: 'var(--font-body)', fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(48,43,44,0.6)',
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '28px', height: '28px', borderRadius: '50%', color: '#F67425',
              border: '1px solid rgba(246,116,37,0.4)', boxShadow: '0 0 0 3px rgba(246,116,37,0.12)',
            }}>{t.icon}</span>{t.text}
          </span>
        ))}
      </motion.div>
    </div>
  </section>
);

export default Hero;
